import type { FC, ReactNode } from 'react'

import { useId, useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export const FITMENT_PREFILL_EVENT = 'fitment:prefill'

export interface FitmentPrefill {
    diameter: string
    spec: string
}

const DIAMETERS = [19, 20, 21, 22, 23]
const SCALE = 30
const CENTER = 280

const formatOffset = (offset: number): string => (offset > 0 ? `+${offset}` : String(offset))

const profileFor = (front: number): string => {
    if (front >= 4.6) return 'Super deep concave'
    if (front >= 3.8) return 'Deep concave'
    if (front >= 3.0) return 'Medium concave'
    return 'Flat face'
}

// Interactive cross-section: see how width and offset move the mounting face and deepen the concave
const FitmentLab: FC = (): ReactNode => {
    const [diameter, setDiameter] = useState(21)
    const [width, setWidth] = useState(10.5)
    const [offset, setOffset] = useState(25)
    const widthId = useId()
    const offsetId = useId()

    const total = width + 1
    const backspacing = total / 2 + offset / 25.4
    const front = total - backspacing
    const profile = profileFor(front)
    const spec = `${diameter} x ${width.toFixed(1)} ET ${formatOffset(offset)}`

    const xInner = CENTER - (total * SCALE) / 2
    const xOuter = CENTER + (total * SCALE) / 2
    const xMount = CENTER + (offset / 25.4) * SCALE
    const reach = xOuter - xMount
    const upperFace = `M ${xOuter - 6} 56 C ${xOuter - 6 - reach * 0.18} 104, ${xMount + reach * 0.32} 112, ${xMount + 4} 120`
    const lowerFace = `M ${xOuter - 6} 244 C ${xOuter - 6 - reach * 0.18} 196, ${xMount + reach * 0.32} 188, ${xMount + 4} 180`

    const request = (): void => {
        const detail: FitmentPrefill = { diameter: String(diameter), spec }
        window.dispatchEvent(new CustomEvent(FITMENT_PREFILL_EVENT, { detail }))
        document.getElementById('commission')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    return (
        <div className='mt-20 grid gap-10 rounded-xl border border-white/5 bg-slate-deep/80 p-6 sm:p-10 lg:grid-cols-[1.15fr_1fr] lg:gap-14'>
            <div>
                <p className='font-mono text-[11px] uppercase tracking-[0.3em] text-gold'>
                    Fitment lab
                </p>
                <h3 className='mt-4 font-heading text-xl font-bold leading-snug text-white sm:text-2xl'>
                    See your offset before it is forged.
                </h3>
                <p className='mt-4 max-w-md text-sm leading-relaxed text-titanium-light'>
                    Move the width and offset to watch the mounting face travel and the concave deepen. Lower offsets push the face outward for a deeper dish.
                </p>

                <figure className='mt-8 rounded-lg border border-white/5 bg-obsidian p-3 sm:p-5'>
                    <svg viewBox='0 0 560 300' className='h-auto w-full' role='img' aria-label={`Cross-section of a ${spec} wheel with a ${profile.toLowerCase()} face`}>
                        <line x1={CENTER} y1='18' x2={CENTER} y2='282' stroke='#8a95a5' strokeOpacity='0.5' strokeDasharray='4 6' />
                        <text x={CENTER} y='14' textAnchor='middle' fill='#8a95a5' fontSize='10' letterSpacing='2' style={{ fontFamily: 'var(--font-mono)' }}>CL</text>

                        <g className='transition-all duration-300'>
                            <path d={`M ${xInner} 40 V 60 H ${xOuter} V 40`} fill='none' stroke='#c4ccd6' strokeWidth='3' />
                            <path d={`M ${xInner} 260 V 240 H ${xOuter} V 260`} fill='none' stroke='#c4ccd6' strokeWidth='3' />
                            <rect x={xOuter - 3} y='30' width='8' height='30' rx='3' fill='#eef1f5' />
                            <rect x={xOuter - 3} y='240' width='8' height='30' rx='3' fill='#eef1f5' />
                            <rect x={xInner - 5} y='32' width='8' height='28' rx='3' fill='#8a95a5' />
                            <rect x={xInner - 5} y='240' width='8' height='28' rx='3' fill='#8a95a5' />

                            <path d={upperFace} fill='none' stroke='#8a95a5' strokeWidth='7' strokeLinecap='round' />
                            <path d={lowerFace} fill='none' stroke='#8a95a5' strokeWidth='7' strokeLinecap='round' />
                            <path d={upperFace} fill='none' stroke='#eef1f5' strokeWidth='1.5' strokeLinecap='round' />
                            <path d={lowerFace} fill='none' stroke='#eef1f5' strokeWidth='1.5' strokeLinecap='round' />

                            <rect x={xMount - 3} y='116' width='7' height='68' rx='2' fill='#ffd700' />
                        </g>

                        <line x1={CENTER} y1='150' x2={xMount} y2='150' stroke='#ffd700' strokeWidth='1.5' />
                        <text x={(CENTER + xMount) / 2} y='143' textAnchor='middle' fill='#ffd700' fontSize='11' style={{ fontFamily: 'var(--font-mono)' }}>
                            ET {formatOffset(offset)}
                        </text>

                        <line x1={xInner} y1='288' x2={xMount} y2='288' stroke='#8a95a5' strokeWidth='1' />
                        <line x1={xMount} y1='292' x2={xOuter} y2='292' stroke='#ffd700' strokeOpacity='0.7' strokeWidth='1' />
                        <text x={xInner} y='280' fill='#8a95a5' fontSize='10' letterSpacing='1.5' style={{ fontFamily: 'var(--font-mono)' }}>CAR SIDE</text>
                        <text x={xOuter} y='280' textAnchor='end' fill='#c4ccd6' fontSize='10' letterSpacing='1.5' style={{ fontFamily: 'var(--font-mono)' }}>FACE</text>
                    </svg>
                    <figcaption className='mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-titanium'>
                        Indicative profile. Exact fitment is confirmed from your measurements.
                    </figcaption>
                </figure>
            </div>

            <div className='flex flex-col'>
                <fieldset>
                    <legend className='font-mono text-[11px] uppercase tracking-[0.24em] text-titanium-light'>
                        Diameter
                    </legend>
                    <div className='mt-3 grid grid-cols-5 gap-2'>
                        {DIAMETERS.map((size) => (
                            <button
                                key={size}
                                type='button'
                                onClick={() => setDiameter(size)}
                                aria-pressed={diameter === size}
                                className={cn(
                                    'rounded-sm border py-2.5 font-heading text-sm font-bold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold',
                                    diameter === size ? 'border-gold bg-gold text-obsidian' : 'border-white/10 text-white hover:border-gold/60'
                                )}
                            >
                                {size}&quot;
                            </button>
                        ))}
                    </div>
                </fieldset>

                <div className='mt-8'>
                    <div className='flex items-baseline justify-between'>
                        <label htmlFor={widthId} className='font-mono text-[11px] uppercase tracking-[0.24em] text-titanium-light'>
                            Width
                        </label>
                        <output htmlFor={widthId} className='font-heading text-lg font-bold text-white'>
                            {width.toFixed(1)}&quot;
                        </output>
                    </div>
                    <input
                        id={widthId}
                        type='range'
                        min={8.5}
                        max={12.5}
                        step={0.5}
                        value={width}
                        onChange={(event) => setWidth(Number(event.target.value))}
                        className='mt-3 w-full accent-gold'
                    />
                </div>

                <div className='mt-8'>
                    <div className='flex items-baseline justify-between'>
                        <label htmlFor={offsetId} className='font-mono text-[11px] uppercase tracking-[0.24em] text-titanium-light'>
                            Offset
                        </label>
                        <output htmlFor={offsetId} className='font-heading text-lg font-bold text-gold'>
                            ET {formatOffset(offset)}
                        </output>
                    </div>
                    <input
                        id={offsetId}
                        type='range'
                        min={-15}
                        max={45}
                        step={1}
                        value={offset}
                        onChange={(event) => setOffset(Number(event.target.value))}
                        className='mt-3 w-full accent-gold'
                    />
                    <div className='mt-2 flex justify-between font-mono text-[10px] tracking-[0.2em] text-titanium'>
                        <span>ET -15</span>
                        <span>ET +45</span>
                    </div>
                </div>

                <dl className='mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-white/5 bg-white/5'>
                    <div className='bg-obsidian p-4'>
                        <dt className='font-mono text-[10px] uppercase tracking-[0.22em] text-titanium'>Backspacing</dt>
                        <dd className='mt-2 font-heading text-lg font-bold text-white'>{backspacing.toFixed(2)}&quot;</dd>
                    </div>
                    <div className='bg-obsidian p-4'>
                        <dt className='font-mono text-[10px] uppercase tracking-[0.22em] text-titanium'>Front spacing</dt>
                        <dd className='mt-2 font-heading text-lg font-bold text-white'>{front.toFixed(2)}&quot;</dd>
                    </div>
                    <div className='col-span-2 bg-obsidian p-4'>
                        <dt className='font-mono text-[10px] uppercase tracking-[0.22em] text-titanium'>Face profile</dt>
                        <dd className='mt-2 font-heading text-lg font-bold text-gold'>{profile}</dd>
                    </div>
                </dl>

                <button
                    type='button'
                    onClick={request}
                    className='group mt-8 inline-flex items-center justify-center gap-3 rounded-sm bg-gold px-6 py-4 font-mono text-xs font-semibold uppercase tracking-[0.24em] text-obsidian transition-colors hover:bg-[#ffe34d] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold'
                >
                    Request {spec}
                    <ArrowRight className='size-4 transition-transform group-hover:translate-x-1' />
                </button>
            </div>
        </div>
    )
}

export default FitmentLab
